import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, Search, UserCheck, UserX, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatError, DAYS, DAY_SHORT } from "@/lib/api";
import { PageTitle } from "./AdminApp";

const todayKey = () => {
  const t = new Date().toISOString().slice(0, 10);
  return DAYS.includes(t) ? t : DAYS[0];
};

const HouseholdCard = ({ r, day, onSet }) => {
  const status = r.checkins?.[day];
  const planned = r.days.includes(day);
  return (
    <div data-testid={`checkin-card-${r.id}`} className={`rounded-lg border bg-neutral-900 p-4 transition-colors ${status === "present" ? "border-emerald-600" : status === "absent" ? "border-red-800" : "border-neutral-800"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-display text-xl text-neutral-50">{r.name}</p>
          <p className="truncate text-xs text-neutral-500">{r.phone} · {r.email}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-neutral-800 px-2.5 py-1 text-sm font-semibold text-amber-300">
          <Users className="h-3.5 w-3.5" /> {r.party_size}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {DAYS.map((d) => (
          <span key={d} className={`rounded px-1.5 py-0.5 text-[11px] ${r.checkins?.[d] === "present" ? "bg-emerald-700 text-white" : r.checkins?.[d] === "absent" ? "bg-red-900 text-red-200" : r.days.includes(d) ? "bg-neutral-800 text-neutral-300" : "bg-neutral-950 text-neutral-600"}`}>
            {DAY_SHORT[d]}
          </span>
        ))}
        {!planned && <span className="text-[11px] text-amber-500">Not planned for this day</span>}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          onClick={() => onSet(r, status === "present" ? "clear" : "present")}
          data-testid={`checkin-present-${r.id}`}
          aria-pressed={status === "present"}
          className={`flex h-12 items-center justify-center gap-2 rounded-md border font-display text-base transition-colors ${status === "present" ? "border-emerald-500 bg-emerald-600 text-white" : "border-neutral-700 text-neutral-300 hover:border-emerald-600"}`}
        >
          <UserCheck className="h-5 w-5" /> Present
        </button>
        <button
          onClick={() => onSet(r, status === "absent" ? "clear" : "absent")}
          data-testid={`checkin-absent-${r.id}`}
          aria-pressed={status === "absent"}
          className={`flex h-12 items-center justify-center gap-2 rounded-md border font-display text-base transition-colors ${status === "absent" ? "border-red-600 bg-red-800 text-white" : "border-neutral-700 text-neutral-300 hover:border-red-700"}`}
        >
          <UserX className="h-5 w-5" /> Absent
        </button>
      </div>
    </div>
  );
};

export default function CheckIn() {
  const [day, setDay] = useState(todayKey);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const id = setTimeout(() => {
      api.get("/admin/checkin", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const onSet = async (r, status) => {
    try {
      const { data } = await api.post(`/admin/registrations/${r.id}/checkin`, { day, status });
      setItems((list) => list.map((x) => (x.id === data.id ? data : x)));
      toast.success(status !== "clear" ? `${r.name}: ${status} (${DAY_SHORT[day]})` : `${r.name}: cleared`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const presentCount = items.filter((r) => r.checkins?.[day] === "present").length;
  const [printDay, setPrintDay] = useState(todayKey);
  const openPrint = () => window.open(`/admin/print?day=${printDay}`, "_blank", "noopener");

  return (
    <div data-testid="admin-checkin">
      <PageTitle title="Check-in">
        <select value={printDay} onChange={(e) => setPrintDay(e.target.value)} className="h-10 rounded-full border border-neutral-800 bg-neutral-900 px-3 text-sm" data-testid="print-day-select">
          {DAYS.map((d) => <option key={d} value={d}>{DAY_SHORT[d]}</option>)}
          <option value="all">All days</option>
        </select>
        <button onClick={openPrint} className="flex h-10 items-center gap-2 rounded-full border border-amber-500/50 px-4 text-sm font-semibold text-amber-400 transition-colors hover:bg-amber-500 hover:text-neutral-950" data-testid="print-sheet-open-button">
          <Printer className="h-4 w-4" /> Print sheet
        </button>
      </PageTitle>
      <div className="mb-4 grid grid-cols-4 gap-1 rounded-lg border border-neutral-800 bg-neutral-900 p-1">
        {DAYS.map((d) => (
          <button key={d} onClick={() => setDay(d)} data-testid={`checkin-day-${d}`} className={`rounded-md py-2.5 font-display text-sm transition-colors ${day === d ? "bg-amber-500 text-neutral-950" : "text-neutral-400 hover:text-neutral-100"}`}>
            {DAY_SHORT[d]}
          </button>
        ))}
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search household by name, phone or email" className="h-12 border-neutral-800 bg-neutral-900 pl-10 text-base" data-testid="checkin-search-input" />
      </div>
      <p className="mb-3 text-sm text-neutral-500" data-testid="checkin-summary">{items.length} household(s) shown · {presentCount} present on {DAY_SHORT[day]}</p>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.map((r) => <HouseholdCard key={r.id} r={r} day={day} onSet={onSet} />)}
      </div>
      {items.length === 0 && <p className="py-10 text-center text-neutral-500" data-testid="checkin-empty">No households found.</p>}
    </div>
  );
}
